import { useQueryClient } from "@tanstack/react-query";

import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { post } from "@/utils/api-handler";
import { toast } from "sonner";
import type { CommandAgentList } from "@/types/command-agent";

interface ActivateCommandAgentVariables {
    agentId: string;
    name?: string;
    previousAgentId?: string | null;
}

interface CommandAgentsInfiniteCache {
    pages: CommandAgentList[];
    pageParams: unknown[];
}

interface UseActivateCommandAgentOptions {
    onError?: (variables: ActivateCommandAgentVariables) => void;
}

export function useActivateCommandAgent(options: UseActivateCommandAgentOptions = {}) {
    const queryClient = useQueryClient();

    return useProtectedMutation<
        unknown,
        { response?: { data?: { message?: string } } },
        ActivateCommandAgentVariables,
        {
            previousSidebarData: Array<[unknown, CommandAgentsInfiniteCache | undefined]>;
        }
    >({
        mutationFn: (data) =>
            post(`/api/command-team/agents/${data.agentId}/activate`),
        requiredPermissions: ["UPDATE_COMMAND_AGENT"],
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: ["command-agents-sidebar"] });

            const previousSidebarData = queryClient.getQueriesData<CommandAgentsInfiniteCache>({
                queryKey: ["command-agents-sidebar"],
            });

            queryClient.setQueriesData<CommandAgentsInfiniteCache>(
                { queryKey: ["command-agents-sidebar"] },
                (old) => {
                    if (!old?.pages) return old;
                    return {
                        ...old,
                        pages: old.pages.map((page) => ({
                            ...page,
                            items: page.items.map((item) => ({
                                ...item,
                                is_selected: item.id === variables.agentId,
                            })),
                        })),
                    };
                }
            );

            return { previousSidebarData };
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["command-agents-sidebar"] });
            queryClient.invalidateQueries({ queryKey: ["command-agents"] });
            queryClient.invalidateQueries({ queryKey: ["command-agents-grid"] });

            const label = variables.name ?? "Agent";
            toast.success(`${label} activated`, {
                description: "Agent is now active and ready to chat.",
            });
        },
        onError: (_error, variables, context) => {
            context?.previousSidebarData?.forEach(([queryKey, data]) => {
                queryClient.setQueryData(queryKey as readonly unknown[], data);
            });

            toast.error("Failed to activate agent", {
                description: `Could not activate ${variables.name ?? "agent"}. Please try again.`,
            });
            options.onError?.(variables);
        },
    });
}
